import type { DeliveryVo } from "@zhihaoscm/entity";
import { ORDER_TYPE, TRANSPORT_ORDER_SHIP_STATE } from "@zhihaoscm/meta";
import { formatCurrency } from "@zhihaoscm/utils";
import dayjs from "dayjs";
import { useState } from "react";
import { BaseItem, Grid } from "zhihao-ui-react";
import { useFullAreaName } from "@/runtime/areaData";
import { AddressMapDialog } from "./OrderLegacyDialogs";

/** Vue formatCurrency 的空值在详情中统一回退为占位符。 */
const money = (value?: number) => formatCurrency(value, "--");
/** 船名按旧版 getShipInfoName 输出“名称/MMSI”。 */
const shipName = (data: DeliveryVo) => {
    const name = data.delivery.shipInfo?.cnname || data.delivery.shipInfo?.name;
    return name ? `${name}/${data.delivery.shipId}` : data.delivery.shipId || "--";
};

/** 逐项迁移自旧 delivery/components/deliveryInfo.vue。 */
export function DeliveryInfo({ data, embedded = false }: { data: DeliveryVo; embedded?: boolean }) {
    const [mapOpen, setMapOpen] = useState(false);
    const delivery = data.delivery;
    const geo = delivery.addressInfo?.geo as { lat?: number; lng?: number } | undefined;
    const areaName = useFullAreaName(delivery.addressInfo?.province, delivery.addressInfo?.city, delivery.addressInfo?.district);
    const address = delivery.addressInfo?.province ? `${areaName}${delivery.addressInfo?.detailAddress || ""}` : "--";
    const hasGeo = geo?.lng != null && geo.lat != null;
    return (
        <section className={`section-card delivery-detail-section${embedded ? " delivery-detail-section--embedded" : ""}`} id="delivery-info">
            {embedded ? null : <h2 className="section-title">提货信息</h2>}
            <div className="delivery-info__message">
                <Grid cols={4} gap={[12, 16]}>
                    <BaseItem label="提货日期" value={delivery.scheduleDate ? dayjs(delivery.scheduleDate).format("YYYY-MM-DD") : "--"} />
                    <BaseItem label="结算金额" value={delivery.settlementAmount == null ? "--" : `${money(delivery.settlementAmount)}元`} />
                    <BaseItem label="提货吨数" labelWidth={98} value={delivery.estimatedTon == null ? "--" : `${money(delivery.estimatedTon)}吨`} />
                    <BaseItem label="结算单价" value={delivery.settlementUnitAmount == null ? "--" : `${money(delivery.settlementUnitAmount)}元/吨`} />
                    <BaseItem label="关联运单">
                        {data.transportOrderShip ? <><button className="delivery-info__link" onClick={() => window.open(`/console/logistics/shipOrder/detail?id=${delivery.transportOrderShipId}`, "_blank")} type="button">{data.transportOrderShip.id}</button><span> ({TRANSPORT_ORDER_SHIP_STATE.getIntroByCode(data.transportOrderShip.state)})</span></> : "--"}
                    </BaseItem>
                    <BaseItem label="船舶">
                        {delivery.shipInfo ? <button className="delivery-info__link" onClick={() => window.open(`/console/logistics/findLocation?mmsi=${delivery.shipId}`, "_blank")} type="button">{shipName(data)}</button> : "--"}
                    </BaseItem>
                    <BaseItem label="关联订单" labelWidth={98}>
                        {delivery.orderId ? <button className="delivery-info__link" onClick={() => window.open(`/console/orderCenter/myOrder/detail/${delivery.orderId}`, "_blank")} type="button">{delivery.orderId}</button> : "--"}
                    </BaseItem>
                    <BaseItem label="订单类型" value={ORDER_TYPE.getIntroByCode(delivery.productType) || "--"} />
                    <BaseItem label="关联排期" value={delivery.scheduleId || "--"} />
                    <BaseItem label="收货人" value={delivery.addressInfo?.contacts || "--"} />
                    <BaseItem label="收货联系方式" labelWidth={98} span={2} value={delivery.addressInfo?.cellphone || "--"} />
                    <BaseItem label="收货地址" span={4}>
                        <span>{address}</span>
                        {hasGeo ? <button aria-label="查看收货地址地图" className="delivery-info__map" onClick={() => setMapOpen(true)} type="button"><i className="iconfont icon-a-Frame1312316869-1" /></button> : null}
                    </BaseItem>
                </Grid>
            </div>
            <AddressMapDialog address={delivery.addressInfo} label="提货收货位置" onClose={() => setMapOpen(false)} open={mapOpen} />
        </section>
    );
}
